import { useLocation, useNavigate } from "react-router-dom"
import {
  MapPin,
  Star,
  Phone,
  Play,
  Timer,
  Clock3,
  Info,
  CircleAlert,
  PawPrint,
  PartyPopper,
  ArrowRight,
  Bed,
  Bath,
  Waves,
  Lightbulb,
  AirVent,
  Wifi,
  Check,
  X,
  Heart,
} from "lucide-react"
import Header from "../components/Header"
import Footer from "../components/Footer"

const placeholderStay = {
  id: "house-40",
  title: "House 40 Residence",
  location: "Basic Estate Lokogoma, Abuja.",
  price: "N150,000",
  description:
    "Step into a warm, modern space designed for comfort and relaxation. This 2-bedroom apartment offers a quiet retreat in the heart of Lekki, perfect for solo travelers, couples, or small families. The interior blends minimalist decor with homely touches — from cozy bedding and ambient lighting to a fully equipped kitchen where you can cook your meals just the way you like them. Whether you're in town for business, leisure, or a weekend getaway, you'll appreciate the seamless mix of functionality and comfort. Enjoy reliable WiFi for work or streaming, unwind in the spacious living area after a day out, or step outside and find yourself within walking distance of local cafes, supermarkets, and vibrant spots around Lekki. Your stay is made smoother with flexible check-in options, thoughtful amenities, and a responsive host committed to making your visit worry-free.",
  features: ["3 Beds", "2 Baths", "Swimming pool", "24/7 Power Supply", "Air Conditioning", "Wifi"],
  rules: ["Check-in: 02:00PM-12:00AM", "Check-out: 12:00PM", "No Smoking", "No Pets", "No Parties"],
  host: {
    name: "Olakode Martins",
    rating: "4.6",
    note: "Responsive, friendly and committed to a 5-star experience",
    avatar: "/images/member.jpg",
  },
  mainImage: "/images/house1.jpg",
  gallery: ["/images/house1.jpg", "/images/house1.jpg", "/images/house1.jpg", "/images/house1.jpg", "/images/house1.jpg", "/images/house1.jpg"],
  videoCover: "/images/house1.jpg",
}

const reviewList = [
  {
    name: "Gabriel Foster",
    date: "10th August, 2024",
    rating: "4.5",
    avatar: "/images/member.jpg",
    text:
      "This cozy yet functional space is designed for your comfort and convenience, offering everything you need for a comfortable stay. You'll find a welcoming atmosphere, with ample natural light brightening up the room.",
  },
  {
    name: "Anonymous",
    date: "10th August, 2024",
    rating: "4.5",
    avatar: "/images/member.jpg",
    text:
      "This cozy yet functional space is designed for your comfort and convenience, offering everything you need for a comfortable stay. You'll find a welcoming atmosphere, with ample natural light brightening up the room.",
  },
]

const similarApartments = Array.from({ length: 4 }, (_, i) => ({
  ...placeholderStay,
  id: `similar-${i}`,
}))

export default function StayDetails() {
  // const { id } = useParams()
  const location = useLocation()
  const navigate = useNavigate()

  const stay = {
    ...placeholderStay,
    ...(location.state || {}),
    gallery: (location.state && location.state.gallery) || placeholderStay.gallery,
    features: (location.state && location.state.features) || placeholderStay.features,
    rules: (location.state && location.state.rules) || placeholderStay.rules,
    host: {
      ...placeholderStay.host,
      ...(location.state && location.state.host ? location.state.host : {}),
    },
  }
  const similar = similarApartments

  return (
    <div className="min-h-screen bg-white text-gray-900">
      <Header />

      <main className="w-full max-w-[90%] mx-auto space-y-12 md:space-y-16 lg:px-0 lg:max-w-[80%] pb-16 pt-10">
        <div className="space-y-6">
          <header className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
          <div>
            <h1 className="text-3xl font-semibold text-[#61013b]">{stay.title}</h1>
            <div className="mt-2 flex items-center gap-2 text-sm font-medium text-gray-600">
              <MapPin className="h-4 w-4  text-gray-500" />
              <span >{stay.location}</span>
            </div>
          </div>
          <div className="text-right">
            <p className="text-xl font-semibold text-[#61013b]">{stay.price}</p>
          </div>
        </header>

        <section className=" grid gap-8 lg:grid-cols-[2fr,1fr]">
          <div className="space-y-10">
            <img src={stay.mainImage} alt={stay.title} className="w-full h-[32rem] rounded-3xl object-cover " />
          <div className="grid grid-cols-6 gap-3">
            {stay.gallery.map((img, idx) => (
              <img key={idx} src={img} alt={`${stay.title} ${idx + 1}`} className="h-32 w-full rounded-lg object-cover" />
            ))}
          </div>
          </div>

          <aside className="space-y-4">
            <div className="rounded-3xl border border-gray-200 p-7 shadow-sm space-y-6">
              <div>
                  <h3 className="mb-4 text-lg font-semibold text-gray-800">Property Rules</h3>
                  <hr className=""/>
              </div>

              {(() => {
                const timeRules = stay.rules.filter((r) => {
                  const lower = r.toLowerCase()
                  return lower.includes("check-in") || lower.includes("check-out")
                })
                const otherRules = stay.rules.filter((r) => !timeRules.includes(r))

                const renderRule = (rule) => {
                  const lower = rule.toLowerCase()
                  let Icon = Info
                  if (lower.includes("check-in")) Icon = Timer
                  else if (lower.includes("check-out")) Icon = Clock3
                  else if (lower.includes("smoking")) Icon = CircleAlert
                  else if (lower.includes("pet")) Icon = PawPrint
                  else if (lower.includes("parties") || lower.includes("party")) Icon = PartyPopper

                  return (
                    <li key={rule} className="flex items-center gap-3">
                      <Icon className="h-5 w-5 text-gray-400" />
                      <span className="font-medium text-gray-600">{rule}</span>
                    </li>
                  )
                }

                return (
                  <div className="space-y-4 text-base text-gray-700">
                    <ul className="space-y-3">{timeRules.map(renderRule)}</ul>
                    {timeRules.length > 0 && otherRules.length > 0 && <hr className="border-gray-200" />}
                    <ul className="space-y-3">{otherRules.map(renderRule)}</ul>
                  </div>
                )
              })()}
            </div>

            <div className="space-y-6 rounded-3xl border border-gray-200 p-5 shadow-sm">
              <div>
                  <h3 className="mb-4 text-lg font-semibold text-gray-800">About your Host</h3>
                  <hr className=""/>
              </div>
              <div className="mt-3 flex items-center gap-3">
                <img src={stay.host.avatar} alt={stay.host.name} className="h-12 w-12 rounded-full object-cover" />
                <div>
                  <div className="flex justify-between items-center">
                    <p className="font-semibold text-gray-700">{stay.host.name}</p>
                  <div className="flex items-center gap-1 font-medium text-gray-600">
                    <span>{stay.host.rating}</span>
                    <Star className="h-4 w-4 text-amber-400 fill-amber-400" />
                  </div>
                  </div>
                  <p className="mt-2 text-sm text-gray-600 font-medium">{stay.host.note}</p>

                </div>
              </div>
              <hr />
              <div className="mt-4 flex flex-col gap-2 items-end">
                <button className="flex w-full items-center justify-center gap-2 rounded-lg border border-[#61013b] px-4 py-3 text- font-medium text-[#61013b]">
                  <Phone className="h-4 w-4" />
                  Contact Host
                </button>
                
              </div>
            </div>
            <button className="w-full flex items-center justify-center gap-3 rounded-lg bg-[#61013b] px-5 py-3 font-medium text-white ">
              <span>Book Now</span>
              <ArrowRight className="h-4 w-4 animate-pulse" />
            </button>
          </aside>
        </section>
        </div>


        <section className="max-w-5xl mt-10 space-y-8">
          <div className="space-y-3">
            <h2 className="text-3xl font-semibold text-[#61013b]">Property Description</h2>
            <p className="text-sm font-medium leading-relaxed text-gray-700">{stay.description}</p>
          </div>
          <hr />
          <div className="flex flex-wrap gap-3 pt-3">
            {stay.features.map((feature) => {
              const lower = feature.toLowerCase()
              let Icon = Info
              if (lower.includes("bed")) Icon = Bed
              else if (lower.includes("bath")) Icon = Bath
              else if (lower.includes("pool")) Icon = Waves
              else if (lower.includes("24/7 power")) Icon = Lightbulb
              else if (lower.includes("conditioning") || lower.includes("ac")) Icon = AirVent
              else if (lower.includes("wifi")) Icon = Wifi

              return (
                <span
                  key={feature}
                  className="inline-flex items-center gap-2 rounded-2xl bg-gray-100 px-5 py-3 text-sm font-medium text-gray-700"
                >
                  <Icon className="h-5 w-5 text-gray-700" />
                  {feature}
                </span>
              )
            })}
          </div>
                    <hr />

        <div className="mt-10 grid gap-6 md:grid-cols-4">
          <div>
            <h3 className="mb-6 text-lg font-semibold text-[#61013b]">Surroundings</h3>
            <ul className="space-y-4 text-sm font-medium text-gray-900">
              {["Water Park", "Grocery Store", "Theme Park", "Airport"].map((item) => (
                <li key={item} className="flex items-start gap-2">
                  <Check className="h-4 w-4 text-[#61013b]" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h3 className="mb-6 text-lg font-semibold text-[#61013b]">Outdoors</h3>
            <ul className="space-y-4 text-sm font-medium text-gray-900">
              {["Heat Lamp", "Hot Tub", "Barbecue", "Lounge"].map((item) => (
                <li key={item} className="flex items-start gap-2">
                  <Check className="h-4 w-4 text-[#61013b]" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h3 className="mb-6 text-lg font-semibold text-[#61013b]">All Services</h3>
            <ul className="space-y-4 text-sm font-medium text-gray-900">
              {["House keeping", "Concierge Services", "Apartment Pre-Stocking", "24/7 Support", "Guided Tours"].map(
                (item) => (
                  <li key={item} className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-[#61013b]" />
                    <span>{item}</span>
                  </li>
                )
              )}
            </ul>
          </div>
          <div>
            <h3 className="mb-6 text-lg font-semibold text-[#61013b]">Not Included</h3>
            <ul className="space-y-4 text-sm font-medium text-gray-900">
              {[
                "Transportation Services",
                "In-home Spa Services",
                "Cooking/Catering Services",
                "Activities & Workshops",
              ].map((item) => (
                <li key={item} className="flex items-start gap-2">
                  <X className="h-4 w-4 text-[#61013b]" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
        </section>


        <section className="max-w-5xl mt-10 space-y-4">
          <h3 className="text-3xl font-semibold text-[#61013b]">Property Video</h3>
          <div className="relative overflow-hidden rounded-xl shadow-md">
            <img src={stay.videoCover} alt="Property video" className="h-[32rem] w-full object-cover" />
            <button className="absolute inset-0 m-auto flex h-16 w-16 items-center justify-center rounded-full bg-white/90 text-[#61013b] shadow-lg">
              <Play className="h-7 w-7 fill-[#61013b]" />
            </button>
          </div>
        </section>

        <section className="max-w-5xl mt-10 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-xl font-semibold text-[#61013b]">Reviews</h3>
            <div className="text- text-gray-700">
              <span className="font-semibold">4.5</span> ⭐ (3 Reviews) 
            </div>
          </div>
              <hr />
          <div className="">
          {reviewList.map((review, idx) => (
            <div key={idx} className="p-4 flex gap-3 items-center">
              <div className="w-14 h-14 rounded-lg overflow-hidden bg-slate-100 flex-shrink-0">
                <img
                  src={review.avatar || "/images/testimonial-passport.jpg"}
                  alt={review.name}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-base font-semibold text-slate-900">
                  {review.name}
                </div>
                <div className="flex items-center font-medium gap-2 text-sm text-slate-700">
                  <span>{review.date}</span>
                  <span class="w-px h-3 bg-slate-700"></span>
                  <span className="flex items-start gap-1 text-slate-600 font-medium">
                    {review.rating}
                    <Star className="w-4 h-4 fill-amber-500 text-amber-500" />
                  </span>
                </div>
                <p className="text-sm text-slate-700 font-medium leading-relaxed">
                  {review.text}
                </p>
              </div>
            </div>
          ))}
        </div>
        </section>

        <section className="mt-14 space-y-6">
          <h3 className="text-3xl font-semibold text-[#61013b]">Similar Apartments</h3>
          <div className="flex gap-4 overflow-x-auto pb-3 snap-x snap-mandatory sm:grid sm:grid-cols-2 sm:gap-6 sm:overflow-visible sm:pb-0 lg:grid-cols-3 xl:grid-cols-4">
            {similar.map((apt) => (
              <div key={`more-${apt.id}`} className="min-w-[260px] snap-start rounded-2xl border border-gray-200 bg-white shadow-sm sm:min-w-0">
                <div className="relative">
                  <img src={apt.mainImage} alt={apt.title} className="h-44 w-full rounded-t-2xl object-cover" />
                  <span className="absolute left-3 top-3 rounded-full bg-emerald-100 px-3 py-1 text-xs font-medium text-[#1DAC4D] shadow-sm">
                    Available
                  </span>
                  <button className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-full bg-white shadow">
                    <Heart className="h-5 w-5 text-gray-800" />
                  </button>
                </div>
                <div className="space-y-2 px-4 py-4">
                  <p className="text-xl font-medium text-[#32011F]">{apt.title}</p>
                  <div className="flex items-center gap-1 text-sm font-medium text-gray-700">
                    <MapPin className="h-4 w-4 text-gray-400" />
                    <span>{apt.location}</span>
                  </div>
                  <div className="flex items-center justify-between border-y border-gray-200 py-2 text-sm text-gray-700">
                    <div className="flex justify-between gap-4">
                      <div className="flex items-center gap-2">
                        <Bed className="h-4 w-4 text-gray-500" />
                        <span>3 Beds</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Bath className="h-4 w-4 text-gray-500" />
                        <span>2 Baths</span>
                      </div>
                    </div>

                    <div className="flex items-center gap-1">
                      <span className="font-medium">4.5</span>
                      <Star className="h-4 w-4 text-amber-400 fill-amber-400" />
                    </div>
                  </div>
                  <div className="flex items-center justify-between pt-1">
                    <p className="text-base font-semibold text-gray-700">{apt.price}/Night</p>
                    <button
                      type="button"
                      className="text-sm font-medium text-[#61013b] hover:underline"
                      onClick={() => navigate(`/stays/${apt.id}`, { state: apt })}
                    >
                      View
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      </main>

      <Footer />
    </div>
  )
}
